import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { LightBulbIcon } from '@heroicons/react/24/solid';
import { useEffect, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { useTheme } from '@/app/ThemeContext';
import { Button } from '@/components/Button';
import { Dropdown } from '@/components/Dropdown';
import { Container } from '@/components/ui';
import { SearchBar } from '@/components/ui/SearchBar';
import { IntegrationSetupModal } from '@/features/integrations/components';
import {
  getCategoryCounts,
  getCategoryIntegrations,
  getTotalIntegrationsCount,
  INTEGRATION_CATEGORIES,
} from '@/features/integrations/config/categories.config';
import { useFeatureFlag } from '@/features/integrations/hooks';
import { IntegrationCategory } from '@/features/integrations/types/category.types';
import type { SimpleIntegration } from '@/features/integrations/types/integration.types';
import { getIntegrationRegistry } from '@/features/integrations/utils';
import { useIntegration } from '@/hooks/useIntegration';
import SetupModal from '@/sections/SetupModal';
import WaitlistedModal from '@/sections/WaitlistModal';
import { useGlobalState } from '@/state/global.state';
import { Integration, IntegrationMeta, KeyboardListersId } from '@/types';

export const IntegrationDrawer = ({
  initialCategory,
}: {
  initialCategory: string;
}) => {
  const { keyboardListeners } = useGlobalState();
  const [search, setSearch] = useState('');
  const { currentTheme } = useTheme();
  const [selectedCategory, setSelectedCategory] = useState<string>(
    initialCategory || 'All'
  );

  const [setupModalIntegration, setSetupModalIntegration] =
    useState<IntegrationMeta | null>(null);
  const [newSetupIntegration, setNewSetupIntegration] =
    useState<SimpleIntegration | null>(null);

  const {
    data: { connectedIntegrations },
  } = useIntegration();

  // Get registry instance
  const registry = getIntegrationRegistry();

  // Feature flags
  const useNewSystem = useFeatureFlag('USE_NEW_INTEGRATION_SYSTEM');
  const useNewOkta = useFeatureFlag(Integration.okta);
  const useNewTenableVM = useFeatureFlag(Integration.tenablevm);

  const categoryCounts = useMemo(() => getCategoryCounts(), []);
  const totalCount = useMemo(() => getTotalIntegrationsCount(), []);

  const filteredIntegrations = useMemo(() => {
    if (selectedCategory === 'All') {
      return Object.entries(INTEGRATION_CATEGORIES).map(([category, _]) => ({
        category,
        integrations: getCategoryIntegrations(
          category as IntegrationCategory
        ).filter(integration =>
          integration?.name?.toLowerCase?.()?.includes(search.toLowerCase())
        ),
      }));
    }

    return [
      {
        category: selectedCategory,
        integrations: getCategoryIntegrations(
          selectedCategory as IntegrationCategory
        ).filter(integration =>
          integration?.name?.toLowerCase?.()?.includes(search.toLowerCase())
        ),
      },
    ];
  }, [selectedCategory, search]);

  const isIntegrationConnected = (integrationId: string) => {
    return connectedIntegrations.some(conn => conn.member === integrationId);
  };

  const handleConnect = async (integration: IntegrationMeta) => {
    // Check if we should use new system for this integration
    const shouldUseNew =
      useNewSystem ||
      (integration.id === Integration.okta && useNewOkta) ||
      (integration.id === Integration.tenablevm && useNewTenableVM);

    if (shouldUseNew) {
      // Check if integration exists in new registry
      const newIntegration = registry.getIntegration(
        integration.id as Integration
      );
      if (newIntegration && newIntegration.type === 'simple') {
        setNewSetupIntegration(newIntegration as SimpleIntegration);
        return;
      }
    }

    // Fallback to old system
    setSetupModalIntegration(integration);
  };

  const handleSearchChange = (e: string) => {
    setSelectedCategory('All');
    setSearch(e);
  };

  useEffect(() => {
    const categories = ['All', ...Object.keys(INTEGRATION_CATEGORIES)];
    keyboardListeners.add(KeyboardListersId.INTEGRATION_DRAWER, {
      ArrowUp: () => {
        const index = categories.indexOf(selectedCategory);
        if (index === 0) {
          setSelectedCategory(categories[categories.length - 1]);
        } else {
          setSelectedCategory(categories[index - 1]);
        }
      },
      ArrowDown: () => {
        const index = categories.indexOf(selectedCategory);
        if (index === categories.length - 1) {
          setSelectedCategory(categories[0]);
        } else {
          setSelectedCategory(categories[index + 1]);
        }
      },
    });

    return () => {
      keyboardListeners.remove(KeyboardListersId.INTEGRATION_DRAWER);
    };
  }, [selectedCategory, keyboardListeners]);

  return (
    <div className="bg-chariot-background-primary flex h-full flex-col p-6">
      <h1 className="text-chariot-text-primary mb-6 text-2xl font-bold">
        Connect to Chariot
      </h1>
      <div className="flex flex-row justify-between">
        <div className="w-72">
          <SearchBar
            placeholder="Search Integrations"
            value={search}
            onChange={handleSearchChange}
            size="sm"
          />
        </div>
        <div className="bg-chariot-background-secondary text-chariot-text-primary flex items-center gap-2 rounded-md p-2 text-sm">
          <LightBulbIcon className="text-chariot-brand-primary size-5" />
          <span>
            Tip: You can add multiple accounts under the same integration
          </span>
        </div>
      </div>
      <Container className="mt-6 flex flex-row p-0">
        <div className="border-chariot-stroke w-[300px] border-r p-4">
          <button
            onClick={() => setSelectedCategory('All')}
            className={twMerge(
              'flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors',
              selectedCategory === 'All'
                ? 'bg-chariot-background-secondary text-chariot-text-primary'
                : 'hover:bg-chariot-transparent'
            )}
          >
            <span>All</span>
            <span className="text-xs">{totalCount}</span>
          </button>

          {Object.keys(INTEGRATION_CATEGORIES)
            .sort((a, b) => a.localeCompare(b))
            .map(category => (
              <button
                key={category}
                onClick={() =>
                  setSelectedCategory(category as IntegrationCategory)
                }
                className={twMerge(
                  'flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors',
                  selectedCategory === category
                    ? 'bg-chariot-background-secondary text-chariot-text-primary'
                    : 'hover:bg-chariot-transparent'
                )}
              >
                <span>{category}</span>
                <span className="text-xs">
                  {categoryCounts[category as IntegrationCategory]}
                </span>
              </button>
            ))}
        </div>

        <div className="flex-1 overflow-auto p-6">
          <div className="space-y-8">
            {filteredIntegrations.map(({ category, integrations }) =>
              integrations.length === 0 ? null : (
                <section key={category}>
                  <h2 className="text-chariot-text-primary mb-4 text-xl font-semibold">
                    {category}
                  </h2>
                  <div className="flex flex-wrap gap-4">
                    {integrations.map(integration => {
                      const isConnected = isIntegrationConnected(
                        integration.id
                      );
                      return (
                        <div
                          key={integration.id}
                          className="border-chariot-stroke relative w-[280px] rounded-lg border px-4 pt-4"
                        >
                          {isConnected && (
                            <div className="absolute top-2 right-2">
                              <Dropdown
                                onClick={event => event.stopPropagation()}
                                startIcon={
                                  <EllipsisVerticalIcon className="size-5" />
                                }
                                styleType="none"
                                menu={{
                                  items: [
                                    {
                                      label: 'Add',
                                      onClick: () => handleConnect(integration),
                                    },
                                  ],
                                }}
                              />
                            </div>
                          )}
                          <div className="flex h-full flex-row items-start gap-4 pb-4">
                            <img
                              src={`/icons/${currentTheme}/${integration.logo}`}
                              alt={integration.name}
                              className="size-8"
                            />
                            <div className="flex h-full flex-1 flex-col">
                              <h3 className="text-chariot-text-primary text-lg font-semibold">
                                {integration.name}
                              </h3>
                              <p className="text-chariot-text-primary mb-4 flex-1 items-start justify-start text-sm">
                                {integration.description}
                              </p>
                              {isConnected ? (
                                <Button
                                  styleType="none"
                                  className="bg-green-500 font-medium text-white hover:bg-green-600"
                                  label="Connected"
                                  size="xs"
                                  data-testid={`integration-${integration.id}-connected`}
                                />
                              ) : (
                                <Button
                                  styleType="primary"
                                  label="Integrate"
                                  size="xs"
                                  onClick={async () => {
                                    await handleConnect(integration);
                                  }}
                                  data-testid={`integration-${integration.id}-connect`}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )
            )}
          </div>
        </div>
      </Container>

      {/* New integration setup modal */}
      {newSetupIntegration && (
        <IntegrationSetupModal
          integration={newSetupIntegration}
          isOpen={true}
          onClose={() => setNewSetupIntegration(null)}
          onSuccess={() => {
            // Refresh integrations list
            setNewSetupIntegration(null);
            // Show success message
            // TODO: Add toast notification
          }}
        />
      )}

      {/* Old integration setup modal */}
      {setupModalIntegration &&
        (setupModalIntegration.inputs || setupModalIntegration.Markup ? (
          <SetupModal
            isOpen={Boolean(setupModalIntegration)}
            onClose={() => setSetupModalIntegration(null)}
            onComplete={() => setSetupModalIntegration(null)}
            selectedIntegration={undefined}
            formData={{}}
            integration={setupModalIntegration}
          />
        ) : (
          <WaitlistedModal
            open={Boolean(setupModalIntegration)}
            onClose={() => setSetupModalIntegration(null)}
            onComplete={() => setSetupModalIntegration(null)}
            integration={setupModalIntegration}
          />
        ))}
    </div>
  );
};
